import React from "react";
import { Box, Button, Flex, Heading, HStack, Icon, SimpleGrid, Spinner, Stack, Text } from "@chakra-ui/react";
import { FiBookOpen, FiCalendar, FiChevronRight, FiMail, FiMapPin, FiPhone, FiPlus, FiUser, FiUsers } from "react-icons/fi";
import { Link } from "react-router-dom";
import { usePerfilSettings } from "features/perfil/settings/store";
import { useMisHijos } from "features/perfil/hijos/hooks";
import { useCampus } from "features/campus/hooks";
import { formatFullName } from "../../../utils/names";

const GRADES = new Map([
  [2015, "4to"], [2014, "5to"], [2013, "6to"],
  [2012, "7mo"], [2011, "8vo"], [2010, "9no"],
]);

function formatBirthDate(value) {
  if (!value) return "Sin fecha";
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  const parts = new Intl.DateTimeFormat("es-MX", { day: "numeric", month: "long", year: "numeric" }).formatToParts(date);
  const part = (type) => parts.find((item) => item.type === type)?.value || "";
  return `${part("month")} ${part("day")}, ${part("year")}`;
}

function parentData(type, family) {
  const mother = type === "madre";
  return {
    label: mother ? "Mamá" : "Papá",
    nombre: mother ? family?.nombreMama : family?.nombrePapa,
    apellidoPaterno: mother ? family?.apellidoPaternoMama : family?.apellidoPaternoPapa,
    apellidoMaterno: mother ? family?.apellidoMaternoMama : family?.apellidoMaternoPapa,
    telefono: mother ? family?.telefonoMama : family?.telefonoPapa,
    correo: mother ? family?.correoMama : family?.correoPapa,
    color: mother ? "red" : "blue",
  };
}

function ParentRow({ type, family }) {
  const parent = parentData(type, family);
  return <Box asChild>
    <Link to={`/perfil/familia/contacto/${type}`}>
      <HStack py="4" gap="3" borderBottomWidth="1px" borderColor="design.border.subtle" _active={{ bg: "design.bg.muted" }}>
        <Flex boxSize="42px" flexShrink="0" rounded="15px" bg={`${parent.color}.50`} color={`${parent.color}.600`} _dark={{ bg: "design.bg.muted" }} align="center" justify="center"><FiUser/></Flex>
        <Box flex="1" minW="0">
          <Text fontSize="xs" fontWeight="800" color="design.fg.subtle">{parent.label}</Text>
          <Text mt=".5" fontWeight="800" truncate>{formatFullName(parent.nombre, parent.apellidoPaterno, parent.apellidoMaterno) || "Completar datos"}</Text>
          <HStack mt="1" gap="3" color="design.fg.muted" fontSize="xs">
            <HStack gap="1" minW="0"><FiPhone/><Text truncate>{parent.telefono || "Sin teléfono"}</Text></HStack>
            <HStack gap="1" minW="0" display={{ base: "none", sm: "flex" }}><FiMail/><Text truncate>{parent.correo || "Sin correo"}</Text></HStack>
          </HStack>
        </Box>
        <Icon as={FiChevronRight} color="gray.400"/>
      </HStack>
    </Link>
  </Box>;
}

function ChildRow({ child, index }) {
  const colors = ["red", "blue", "red"];
  const color = colors[index % colors.length];
  return <Box asChild>
    <Link to={`/perfil/familia/hijos/${child.id}`}>
      <HStack py="4" gap="3" borderBottomWidth="1px" borderColor="design.border.subtle" _active={{ bg: "design.bg.muted" }}>
        <Flex boxSize="42px" flexShrink="0" rounded="15px" bg={`${color}.50`} color={`${color}.600`} _dark={{ bg: "design.bg.muted" }} align="center" justify="center"><FiUser/></Flex>
        <Box flex="1" minW="0">
          <Text fontWeight="800" truncate>{formatFullName(child.nombre, child.apellidoPaterno, child.apellidoMaterno)}</Text>
          <HStack mt="1" gap="2" color="design.fg.muted" fontSize="xs">
            <Text fontWeight="700">{GRADES.get(Number(child.generacion)) || child.generacion}</Text>
            <Text color="gray.300">/</Text>
            <HStack gap="1"><FiCalendar/><Text>{formatBirthDate(child.fechaNacimiento)}</Text></HStack>
          </HStack>
        </Box>
        <Icon as={FiChevronRight} color="gray.400"/>
      </HStack>
    </Link>
  </Box>;
}

export default function FamiliaPerfil() {
  const perfil = usePerfilSettings();
  const { data: children = [], isLoading } = useMisHijos(perfil.ready);
  const { data: campuses = [], isLoading: campusLoading } = useCampus();
  const busy = !perfil.ready || isLoading || campusLoading;

  if (busy) return <Flex minH="45vh" align="center" justify="center"><Spinner color="brand.red"/></Flex>;

  return <Box pb="4" maxW="1120px" mx="auto">
    <HStack justify="space-between" align="end">
      <Box><Text color="brand.red" fontWeight="800" fontSize="sm">Mi cuenta</Text><Heading mt="1" fontSize="3xl">Familia</Heading></Box>
      <HStack px="3" py="2" rounded="full" bg="brand.redSubtle" color="brand.redFg"><FiUsers/><Text fontSize="xs" fontWeight="800">{children.length} {children.length === 1 ? "hijo" : "hijos"}</Text></HStack>
    </HStack>
    <Text mt="2" color="design.fg.muted" fontSize="sm">Contactos y datos escolares, sin pasos extra.</Text>

    <SimpleGrid mt="7" columns={{ base: 1, lg: 2 }} gap={{ base: "7", lg: "6" }} alignItems="start">

    <Box p={{ lg: "6" }} rounded={{ lg: "24px" }} bg={{ lg: "design.bg.panel" }} borderWidth={{ lg: "1px" }} borderColor="design.border.subtle">
      <HStack justify="space-between"><Heading fontSize="lg">Padres y contactos</Heading><Text fontSize="xs" color="design.fg.subtle">Toca para editar</Text></HStack>
      <Stack mt="2" gap="0">
        <ParentRow type="madre" family={perfil.familia}/>
        <ParentRow type="padre" family={perfil.familia}/>
      </Stack>
    </Box>

    <Box p={{ lg: "6" }} rounded={{ lg: "24px" }} bg={{ lg: "design.bg.panel" }} borderWidth={{ lg: "1px" }} borderColor="design.border.subtle" gridRow={{ lg: "span 2" }}>
      <HStack justify="space-between">
        <Heading fontSize="lg">Hijos</Heading>
        <Button asChild size="sm" rounded="full" bg="brand.red" color="white"><Link to="/perfil/familia/hijos/nuevo"><FiPlus/>Agregar</Link></Button>
      </HStack>
      <Stack mt="2" gap="0">
        {children.map((child, index) => <ChildRow key={child.id} child={child} index={index}/>)}
        {!children.length && <Box py="9" textAlign="center" borderBottomWidth="1px" borderColor="gray.100"><Text color="gray.500" fontSize="sm">{"Todav\u00eda no hay hijos registrados."}</Text></Box>}
      </Stack>
    </Box>

    <Box p={{ lg: "6" }} rounded={{ lg: "24px" }} bg={{ lg: "design.bg.panel" }} borderWidth={{ lg: "1px" }} borderColor="design.border.subtle">
      <HStack justify="space-between"><Heading fontSize="lg">Datos escolares</Heading><Text fontSize="xs" color="design.fg.subtle">Para toda la familia</Text></HStack>
      <Box asChild><Link to="/perfil/familia/escolar"><HStack py="4" gap="3" borderBottomWidth="1px" borderColor="design.border.subtle" _active={{ bg: "design.bg.muted" }}>
        <Flex boxSize="42px" flexShrink="0" rounded="15px" bg="red.50" color="red.600" _dark={{ bg: "red.950", color: "red.300" }} align="center" justify="center"><FiBookOpen/></Flex>
        <Box flex="1" minW="0">
          <Text fontWeight="800" truncate>{perfil.familia?.colegio || children[0]?.colegio || "Completar colegio"}</Text>
          <HStack mt="1" gap="1" color="gray.500" fontSize="xs"><FiMapPin/><Text truncate>{campuses.find((campus) => campus.id === (perfil.familia?.campusId || children[0]?.campusId))?.nombre || children[0]?.campusNombre || "Completar campus"}</Text></HStack>
        </Box>
        <Icon as={FiChevronRight} color="gray.400"/>
      </HStack></Link></Box>
    </Box>
    </SimpleGrid>
  </Box>;
}
